import React from "react";
import { EmailShareButton, FacebookMessengerShareButton, FacebookShareButton, LineShareButton, TelegramShareButton, TwitterShareButton, WhatsappShareButton } from "react-share";
import { useSdkContext } from "~/components/contexts";
import { notEmpty } from "~/lib/utils";
import { CopyLinkButton } from "./copy-link-button";
import { CopyLinkIcon, EmailIcon, EmailIcon1, FacebookIcon, FacebookMessengerIcon, LineIcon, PrintIcon, TelegramIcon, TwitterIcon, WhatsappIcon } from "./icons";
import { PrintButton } from "./print-button";
import { Label, StyledIconContainer } from "./styles";
export const ShareButton = ({
  children,
  className,
  customGATracking,
  disableTrackGA,
  disableTrackMixpanel,
  extraProps,
  hideIcon = false,
  onClick,
  position,
  relativeUrl,
  shortUrl,
  showLabel = false,
  style,
  title,
  type,
  utm
}) => {
  var _extraProps$copyLink, _extraProps$copyLink2, _extraProps$copyLink$, _extraProps$copyLink3;
  const {
    config,
    handleTrackGA,
    handleTrackGA4,
    handleTrackMixPanel,
    userCountry
  } = useSdkContext();
  const absoluteUrl = new URL(shortUrl !== null && shortUrl !== void 0 ? shortUrl : relativeUrl, config.siteUrl);
  const renderIcon = () => {
    var _style$backgroundColo, _style$color, _style$mobilePadding, _style$padding, _style$size;
    if (hideIcon) return null;
    const iconMaps = {
      "copy-link": CopyLinkIcon,
      email: EmailIcon,
      email1: EmailIcon1,
      facebook: FacebookIcon,
      line: LineIcon,
      messenger: FacebookMessengerIcon,
      print: PrintIcon,
      telegram: TelegramIcon,
      twitter: TwitterIcon,
      whatsapp: WhatsappIcon
    };
    const Icon = iconMaps[type];
    return /*#__PURE__*/React.createElement(StyledIconContainer, {
      $backgroundColor: (_style$backgroundColo = style === null || style === void 0 ? void 0 : style.backgroundColor) !== null && _style$backgroundColo !== void 0 ? _style$backgroundColo : "#001246",
      $iconFillColor: (_style$color = style === null || style === void 0 ? void 0 : style.color) !== null && _style$color !== void 0 ? _style$color : "white",
      $mobilePadding: (_style$mobilePadding = style === null || style === void 0 ? void 0 : style.mobilePadding) !== null && _style$mobilePadding !== void 0 ? _style$mobilePadding : 12,
      $padding: (_style$padding = style === null || style === void 0 ? void 0 : style.padding) !== null && _style$padding !== void 0 ? _style$padding : 5,
      $size: (_style$size = style === null || style === void 0 ? void 0 : style.size) !== null && _style$size !== void 0 ? _style$size : 24,
      $svgTransform: style === null || style === void 0 ? void 0 : style.svgTransform,
      "data-qa": "render-icon"
    }, /*#__PURE__*/React.createElement(Icon, null));
  };
  const renderLabel = label => showLabel && /*#__PURE__*/React.createElement(Label, null, label);
  const handleTrackGAEvent = () => {
    var _customGATracking$eve;
    if (disableTrackGA) return;
    const eventLabel = new URL(relativeUrl, config.siteUrl).href;
    const customGAEvent = customGATracking === null || customGATracking === void 0 || (_customGATracking$eve = customGATracking.events) === null || _customGATracking$eve === void 0 ? void 0 : _customGATracking$eve[type];
    if (customGAEvent) {
      handleTrackGA(customGAEvent, {
        untyped: true
      });
      return;
    }
    const shareButtonAction = type => `Share button/${type}/Click`;
    const shareMenuButtonAction = type => `Share menu button/${type}/Click`;
    const eventActions = {
      "copy-link": shareButtonAction("Copy Link"),
      email: shareButtonAction("Email"),
      email1: shareButtonAction("Email"),
      facebook: shareButtonAction("Facebook"),
      line: shareButtonAction("Line"),
      messenger: shareButtonAction("facebook messenger"),
      print: shareMenuButtonAction("Print"),
      telegram: shareButtonAction("Telegram"),
      twitter: shareButtonAction("Twitter"),
      whatsapp: shareButtonAction("WhatsApp")
    };
    const action = eventActions[type];
    if (notEmpty(customGATracking === null || customGATracking === void 0 ? void 0 : customGATracking.actionPrefix) || notEmpty(customGATracking === null || customGATracking === void 0 ? void 0 : customGATracking.category)) {
      var _customGATracking$cat;
      handleTrackGA({
        action: customGATracking !== null && customGATracking !== void 0 && customGATracking.actionPrefix ? `${customGATracking.actionPrefix}/${action}` : action,
        category: (_customGATracking$cat = customGATracking === null || customGATracking === void 0 ? void 0 : customGATracking.category) !== null && _customGATracking$cat !== void 0 ? _customGATracking$cat : "Share button (Adhesive)",
        label: eventLabel
      }, {
        untyped: true
      });
    } else {
      handleTrackGA({
        action,
        category: "Share button (Adhesive)",
        label: eventLabel
      });
    }
  };
  const getTypeShareMethod = () => {
    switch (type) {
      case "copy-link":
        return "copy_link";
      case "messenger":
        return "facebook_messenger";
      case "email1":
        return "email";
      default:
        return type;
    }
  };
  const handleTrackGA4Event = () => {
    const method = getTypeShareMethod();
    const shareUrl = new URL(relativeUrl, config.siteUrl).href;
    if (position) {
      handleTrackGA4({
        action: "click",
        category: "share",
        customized_parameters: {
          method,
          position,
          share_url: shareUrl
        },
        subcategory: ""
      });
    }
  };
  const handleTrackMixpanelEvent = () => {
    if (disableTrackMixpanel) return;
    const shareChannels = {
      "copy-link": "Copy Link",
      email: "Email",
      email1: "Email",
      facebook: "Facebook",
      line: "Line",
      messenger: "Facebook Messenger",
      print: "Print",
      telegram: "Telegram",
      twitter: "Twitter",
      whatsapp: "Whatsapp"
    };
    handleTrackMixPanel({
      name: "Article Share",
      property: {
        Location: "Share Button",
        "Share Channel": shareChannels[type]
      }
    });
  };
  const handleClick = () => {
    handleTrackGAEvent();
    handleTrackGA4Event();
    handleTrackMixpanelEvent();
    onClick === null || onClick === void 0 || onClick();
  };
  const computeAugmentedUrlWithUTMParameters = () => {
    if (!(utm !== null && utm !== void 0 && utm.campaign)) return absoluteUrl.href;
    const augmentedUrl = new URL(absoluteUrl);
    augmentedUrl.searchParams.set("utm_source", type);
    augmentedUrl.searchParams.set("utm_campaign", utm.campaign);
    if (utm.medium) augmentedUrl.searchParams.set("utm_medium", utm.medium);
    if (utm.content) augmentedUrl.searchParams.set("utm_content", utm.content);
    return augmentedUrl.href;
  };
  const augmentedUrl = computeAugmentedUrlWithUTMParameters();
  switch (type) {
    case "copy-link":
      return /*#__PURE__*/React.createElement(CopyLinkButton, {
        className: className,
        disablePortal: extraProps === null || extraProps === void 0 || (_extraProps$copyLink = extraProps.copyLink) === null || _extraProps$copyLink === void 0 ? void 0 : _extraProps$copyLink.disablePortal,
        LabelComponent: Label,
        showLabel: showLabel,
        tooltipPlacement: extraProps === null || extraProps === void 0 || (_extraProps$copyLink2 = extraProps.copyLink) === null || _extraProps$copyLink2 === void 0 ? void 0 : _extraProps$copyLink2.tooltipPlacement,
        url: augmentedUrl,
        variant: (_extraProps$copyLink$ = extraProps === null || extraProps === void 0 || (_extraProps$copyLink3 = extraProps.copyLink) === null || _extraProps$copyLink3 === void 0 ? void 0 : _extraProps$copyLink3.variant) !== null && _extraProps$copyLink$ !== void 0 ? _extraProps$copyLink$ : "responsive",
        onClick: handleClick
      }, renderIcon(), children);
    case "email":
    case "email1":
      const separator = "\r\n";
      const bodyMessage = `${title}${separator}` + `${augmentedUrl}${separator}` + `------------------------------${separator}` + (userCountry === "HK" ? "Join our Telegram to get the latest Hong Kong updates https://sc.mp/join-our-telegram" : "Download the SCMP app to explore our daily digest of Hong Kong news. https://scmp.onelink.me/3586748601/kdat6bde");
      return /*#__PURE__*/React.createElement(EmailShareButton, {
        beforeOnClick: handleClick,
        body: bodyMessage,
        className: className,
        separator: separator,
        subject: "Shared from South China Morning Post",
        url: ""
      }, renderIcon(), renderLabel("Email"), children);
    case "facebook":
      return /*#__PURE__*/React.createElement(FacebookShareButton, {
        className: className,
        url: augmentedUrl,
        onClick: handleClick
      }, renderIcon(), renderLabel("Facebook"), children);
    case "line":
      return /*#__PURE__*/React.createElement(LineShareButton, {
        className: className,
        title: title,
        url: augmentedUrl,
        onClick: handleClick
      }, renderIcon(), renderLabel("Line"), children);
    case "messenger":
      return /*#__PURE__*/React.createElement(FacebookMessengerShareButton, {
        appId: config.facebook.appId,
        className: className,
        title: title,
        url: augmentedUrl,
        onClick: handleClick
      }, renderIcon(), renderLabel("Messenger"), children);
    case "print":
      return /*#__PURE__*/React.createElement(PrintButton, {
        className: className,
        url: relativeUrl,
        onClick: handleClick
      }, renderIcon(), renderLabel("Print"), children);
    case "telegram":
      return /*#__PURE__*/React.createElement(TelegramShareButton, {
        className: className,
        title: `${title}\n\nJoin SCMP\'s Channel: t.me/scmp_news - Get Daily News Update`,
        url: augmentedUrl,
        onClick: handleClick
      }, renderIcon(), renderLabel("Telegram"), children);
    case "twitter":
      return /*#__PURE__*/React.createElement(TwitterShareButton, {
        className: className,
        title: title,
        url: augmentedUrl,
        via: config.twitter.accountName.toLowerCase(),
        onClick: handleClick
      }, renderIcon(), renderLabel("X/Twitter"), children);
    case "whatsapp":
      return /*#__PURE__*/React.createElement(WhatsappShareButton, {
        className: className,
        separator: "\n\n",
        title: title,
        url: augmentedUrl,
        onClick: handleClick
      }, renderIcon(), renderLabel("Whatsapp"), children);
    default:
      return null;
  }
};
ShareButton.displayName = "ShareButton";